import { useState } from 'react';
import type { BreakApproach, BreakState } from '@chore-project/shared';
import { Modal } from './Modal';
import { addDays } from '../lib/date';
import { useSetBreak, useEndBreak } from '../hooks/useBreak';
import { tapLight, celebrate } from '../lib/haptics';

interface BreakSheetProps {
  currentBreak?: BreakState;
  onClose: () => void;
}

const APPROACHES: { value: BreakApproach; icon: string; title: string; sub: string }[] = [
  {
    value: 'pause',
    icon: 'pause',
    title: 'Pause chores',
    sub: 'No new tasks while you’re away — pick up as normal when you’re back.',
  },
  {
    value: 'delay',
    icon: 'time-outline',
    title: 'Delay chores',
    sub: 'They wait for you, all due on your return date.',
  },
];

function todayYmd(): string {
  const d = new Date();
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function formatReturn(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long' }).format(
    new Date(y!, m! - 1, d!),
  );
}

export function BreakSheet({ currentBreak, onClose }: BreakSheetProps) {
  const setBreak = useSetBreak();
  const endBreak = useEndBreak();

  const [approach, setApproach] = useState<BreakApproach | null>(null);
  const [returnDate, setReturnDate] = useState('');

  const minDate = addDays(todayYmd(), 1);

  const start = () => {
    if (!approach || !returnDate) return;
    setBreak.mutate(
      { returnDate, approach },
      {
        onSuccess: () => {
          celebrate();
          onClose();
        },
      },
    );
  };

  const end = () => {
    endBreak.mutate(undefined, {
      onSuccess: () => {
        celebrate();
        onClose();
      },
    });
  };

  // --- Already on a break: confirm ending it -------------------------------
  if (currentBreak) {
    return (
      <Modal title="You're on a break" onClose={onClose}>
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          You’re taking a break until <strong>{formatReturn(currentBreak.returnDate)}</strong>.
          {currentBreak.approach === 'delay'
            ? ' Your chores are waiting for you on that day.'
            : ' Chores will start being added again then.'}
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1rem' }}>
          End your break now? Chores will start being added again from today.
        </p>
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-primary btn-full"
            disabled={endBreak.isPending}
            onClick={end}
          >
            {endBreak.isPending ? 'Ending…' : 'End my break'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Keep my break
          </button>
        </div>
        {endBreak.isError && (
          <div className="alert alert-error" style={{ marginTop: '0.75rem' }}>
            Couldn’t end your break — please try again.
          </div>
        )}
      </Modal>
    );
  }

  // --- Not on a break: set one up ------------------------------------------
  return (
    <Modal title="Take a break" onClose={onClose}>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: 0, marginBottom: '0.85rem' }}>
        Going away or need some time off? Choose how your chores should behave while you’re out.
      </p>

      <div className="break-approach-grid">
        {APPROACHES.map((a) => {
          const selected = approach === a.value;
          return (
            <button
              key={a.value}
              type="button"
              aria-pressed={selected}
              className={`break-approach-card${selected ? ' break-approach-card--selected' : ''}`}
              onClick={() => {
                tapLight();
                setApproach(a.value);
              }}
            >
              <span className="break-approach-top">
                <span className="break-approach-icon"><ion-icon name={a.icon} /></span>
                <span
                  className={`chore-check break-approach-check${selected ? ' chore-check-done' : ''}`}
                  aria-hidden="true"
                >
                  <ion-icon name="checkmark-sharp" />
                </span>
              </span>
              <span className="break-approach-title">{a.title}</span>
              <span className="break-approach-desc">{a.sub}</span>
            </button>
          );
        })}
      </div>

      <div className="field">
        <label htmlFor="break-return">I’ll be back on</label>
        <input
          id="break-return"
          type="date"
          className="input-inline"
          value={returnDate}
          min={minDate}
          onChange={(e) => setReturnDate(e.target.value)}
        />
      </div>

      <div className="form-actions" style={{ marginTop: '1rem' }}>
        <button
          type="button"
          className="btn btn-primary btn-full"
          disabled={!approach || !returnDate || setBreak.isPending}
          onClick={start}
        >
          {setBreak.isPending ? 'Starting…' : 'Start break'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
      {setBreak.isError && (
        <div className="alert alert-error" style={{ marginTop: '0.75rem' }}>
          Couldn’t start your break — please try again.
        </div>
      )}
    </Modal>
  );
}
